import { useState, useRef, useEffect } from 'react';
import { amountsMatch } from '@acitra/shared';

export interface EditableValueProps {
  /** Current value to display (formatted for display mode) */
  value: string | number;
  /** Raw value to show in edit mode (full precision). If not provided, uses value. */
  rawValue?: string | number;
  /** Called when value is saved - can be async. Empty string means field was cleared. */
  onSave: (value: string) => void | Promise<void>;
  /** Optional validation function - return true if valid */
  validate?: (value: string) => boolean;
  /** Whether editing is disabled */
  disabled?: boolean;
  /** Input mode for mobile keyboards */
  inputMode?: 'text' | 'numeric' | 'decimal';
  /** Width of the editable field (Tailwind class, e.g., 'w-20', 'w-32') */
  width?: string;
  /** Text alignment */
  textAlign?: 'left' | 'center' | 'right';
  /** Custom class for additional styling */
  className?: string;
  /** Original value for reset functionality - if different from value, shows reset button */
  originalValue?: string | number;
  /** Called when reset button clicked */
  onReset?: () => void | Promise<void>;
  /** Calculated value suggestion - if provided, shows calculate button */
  calculatedValue?: number;
  /** Called when calculate button clicked */
  onCalculate?: () => void | Promise<void>;
}

// Inline SVG icons
function UndoIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 10h11a5 5 0 010 10h-1M3 10l4-4m-4 4l4 4"
      />
    </svg>
  );
}

function CalculateIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
      />
    </svg>
  );
}

export function EditableValue({
  value,
  rawValue,
  onSave,
  validate,
  disabled = false,
  inputMode = 'text',
  width = 'w-10',
  textAlign = 'right',
  className = '',
  originalValue,
  onReset,
  calculatedValue,
  onCalculate,
}: EditableValueProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Track the value we started editing with, so we can detect if it actually changed
  const initialEditingValue = useRef('');

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const startEditing = () => {
    if (disabled || isSaving) return;
    // Use rawValue for editing if provided (full precision), otherwise use display value
    const editValue = rawValue !== undefined ? rawValue : value;
    // Handle null/undefined: show '0' for numeric fields, empty string for text
    const isNumeric = inputMode === 'decimal' || inputMode === 'numeric';
    const valStr = editValue == null ? (isNumeric ? '0' : '') : String(editValue);
    // If the value is a placeholder hyphen or literal 'null'/'undefined', use appropriate default
    const initial =
      valStr === '-' || valStr === 'null' || valStr === 'undefined'
        ? isNumeric
          ? '0'
          : ''
        : valStr;
    setInputValue(initial);
    initialEditingValue.current = initial;
    setIsEditing(true);
  };

  const save = async () => {
    const trimmedValue = inputValue.trim();
    const isValid = validate ? validate(trimmedValue) : true;

    // Only save if valid AND the value actually changed from what we started editing with
    if (isValid && trimmedValue !== initialEditingValue.current.trim()) {
      setIsSaving(true);
      try {
        // Pass trimmed value - empty string means "explicitly cleared"
        // The API stores "" as an edit, which is different from null (reset to original)
        await onSave(trimmedValue);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
      } catch {
        setShowError(true);
        setTimeout(() => setShowError(false), 2000);
      } finally {
        setIsSaving(false);
      }
    }
    setIsEditing(false);
  };

  const cancel = () => {
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      save();
    } else if (e.key === 'Escape') {
      cancel();
    }
  };

  // Action button handlers
  const handleReset = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onReset) {
      setIsSaving(true);
      try {
        await onReset();
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
      } catch {
        setShowError(true);
        setTimeout(() => setShowError(false), 2000);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleCalculate = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onCalculate) {
      setIsSaving(true);
      try {
        await onCalculate();
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
      } catch {
        setShowError(true);
        setTimeout(() => setShowError(false), 2000);
      } finally {
        setIsSaving(false);
      }
    }
  };

  // Determine if action buttons should be shown
  // Show reset if: onReset provided, originalValue provided, and values differ
  // Don't show if both are placeholders ('-')
  const showReset =
    onReset &&
    originalValue !== undefined &&
    String(originalValue) !== String(value) &&
    !(String(originalValue) === '-' && String(value) === '-');
  // Only show calculate if calculated value differs from current value
  const showCalculate = (() => {
    if (!onCalculate || calculatedValue === undefined) return false;
    // Parse the current value (remove commas for number comparison)
    const currentNumeric = parseFloat(String(value).replace(/,/g, ''));
    if (isNaN(currentNumeric)) return true; // Show if current value isn't a number
    // Use shared amountsMatch for consistent tolerance across the app
    return !amountsMatch(currentNumeric, calculatedValue);
  })();
  const hasActions = showReset || showCalculate;
  const showActions = isHovering && !isEditing && !isSaving && hasActions;

  // Shared styles for consistent sizing between button and input
  const alignClass =
    textAlign === 'left' ? 'text-left' : textAlign === 'center' ? 'text-center' : 'text-right';
  // Use flex-1 for auto width to fill available space, otherwise use specified width
  const widthClass = width === 'w-auto' ? 'flex-1 min-w-0' : width;

  // Success/error state: solid border
  const statusStyles = showSuccess ? 'border-primary-500' : showError ? 'border-red-500' : '';

  // Saving state: pulsing glow
  const savingStyles = isSaving
    ? 'border-primary-500 shadow-[0_0_0_3px_rgba(44,160,28,0.4)] animate-pulse'
    : '';

  const hasStatus = showSuccess || showError || isSaving;
  // Use isHovering state for border instead of CSS hover so it stays active when hovering action buttons
  const borderStyle = hasStatus ? '' : isHovering ? 'border-gray-300' : 'border-transparent';

  // Element styles - w-full to fill wrapper (no text-sm so className can override font size)
  const baseElementStyles = `w-full py-1.5 px-2 font-medium text-gray-700 rounded-md border ${alignClass} transition-all duration-200 box-border`;
  // Check for text size classes (text-xs, text-sm, text-base, text-lg, etc.) but not color classes (text-gray-500, text-edited-600)
  const hasTextSize = /\btext-(xs|sm|base|lg|xl|2xl|3xl)\b/.test(className);
  const elementStyles = hasTextSize ? baseElementStyles : `${baseElementStyles} text-sm`;

  // Action buttons are positioned absolutely above the element.
  // We add a tiny delay to onMouseLeave to prevent flickering if there's a 1px gap or fast movement.

  const hoverTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = undefined;
    }
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovering(false);
    }, 100);
  };

  return (
    <div
      className={`${widthClass} min-w-0 relative`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Action buttons - positioned above the input */}
      {showActions && (
        <div
          className={`absolute bottom-full mb-1 z-10 ${textAlign === 'right' ? 'right-0' : 'left-0'}`}
        >
          <div className="flex gap-0.5 p-1 bg-white rounded-lg shadow-lg border border-gray-200">
            {showReset && (
              <button
                type="button"
                onClick={handleReset}
                className="group/reset relative p-1 rounded hover:bg-gray-100 transition-colors"
                title="Reset to original"
              >
                <UndoIcon className="w-3.5 h-3.5 text-gray-500" />
              </button>
            )}
            {showCalculate && (
              <button
                type="button"
                onClick={handleCalculate}
                className="group/calc relative p-1 rounded hover:bg-gray-100 transition-colors"
                title="Calculate"
              >
                <CalculateIcon className="w-3.5 h-3.5 text-gray-500" />
              </button>
            )}
          </div>
          {/* Arrow pointing down */}
          <div
            className={`absolute top-full w-0 h-0 border-x-[6px] border-x-transparent border-t-[6px] border-t-white ${textAlign === 'right' ? 'right-2' : 'left-2'}`}
          />
          <div
            className={`absolute top-full mt-px w-0 h-0 border-x-[6px] border-x-transparent border-t-[6px] border-t-gray-200 -z-10 ${textAlign === 'right' ? 'right-2' : 'left-2'}`}
          />
        </div>
      )}
      {/* Inner container for button/input */}
      <div className="relative">
        {/* Button determines the size of the container */}
        <button
          type="button"
          onClick={startEditing}
          disabled={disabled || isSaving}
          className={`${elementStyles} ${borderStyle} truncate focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed ${savingStyles} ${statusStyles} ${className} ${isEditing ? 'invisible' : ''}`}
          tabIndex={isEditing ? -1 : 0}
        >
          {value}
        </button>
        {/* Input is absolutely positioned to overlay the button */}
        {isEditing && (
          <input
            ref={inputRef}
            type="text"
            inputMode={inputMode}
            size={1}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onBlur={save}
            onKeyDown={handleKeyDown}
            className={`absolute inset-0 ${elementStyles} border-gray-300 bg-white outline-none focus:border-gray-400 ${savingStyles} ${statusStyles} ${className}`}
          />
        )}
      </div>
    </div>
  );
}
